import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Add, DeleteOutline } from "@mui/icons-material";
import { Button, Chip, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography, ToggleButtonGroup, ToggleButton, Divider, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, IconButton } from "@mui/material";
import { toLocalDateInputValue } from "./utils";
import { EmployeeService } from "../../service/employeeService";
import { LocationService } from "../../service/locationService";
import SimpleTimePicker from "../ui/simpleTimePicker";
import SimpleDatePicker from "../ui/simpleDatePicker";
import { usePreferences } from "hooks/usePreferences";

export default function ScheduleDialog({ open, practitionerId: pid, locationId: lid, mode = "edit", schedules, makeDefaultWeekly, onSaveDraft, onClose }) {
  const { preferences } = usePreferences();
  const tenantTimezone = preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isCreateMode = mode === "create";
  const weeklyBase = isCreateMode
    ? makeDefaultWeekly()
    : ((pid && lid) ? (schedules.get(`${pid}-${lid}`) || makeDefaultWeekly()) : makeDefaultWeekly());
  const todayStr = toLocalDateInputValue(new Date(), tenantTimezone);

  const addDays = (dateLike, days) => {
    const d = new Date(dateLike);
    d.setDate(d.getDate() + days);
    return d;
  };
  const parseLocalDate = (yyyyMmDd) => {
    if (!yyyyMmDd || typeof yyyyMmDd !== "string") return new Date();
    const [y, m, d] = yyyyMmDd.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };
  const toDateString = useCallback((dateLike) => toLocalDateInputValue(new Date(dateLike), tenantTimezone), [tenantTimezone]);
  const defaultNonRepeatEntry = () => ({ date: todayStr, start: "09:00", end: "17:00" });

  const normalizeDraft = useCallback((base) => {
    const start = base?._meta?.start || todayStr;
    const normalizedEnd = (() => {
      const endType = base?._meta?.end?.type || "on";
      if (endType === "on") {
        return {
          type: "on",
          date: base?._meta?.end?.date || toDateString(addDays(parseLocalDate(start), 7)),
        };
      }
      if (endType === "after") {
        return {
          type: "after",
          count: Math.max(1, Number(base?._meta?.end?.count) || 10),
        };
      }
      return { type: "never" };
    })();

    return {
      ...base,
      _meta: {
        ...(base?._meta || {}),
        frequency: base?._meta?.frequency || "weekly",
        start,
        end: normalizedEnd,
        nonRepeatEntries: Array.isArray(base?._meta?.nonRepeatEntries)
          ? base._meta.nonRepeatEntries.filter((entry) => entry?.date && entry?.start && entry?.end)
          : [],
      },
    };
  }, [todayStr, toDateString]);

  const [draft, setDraft] = useState({
    ...normalizeDraft(weeklyBase),
  });

  const [prName, setPrName] = useState("");
  const [locName, setLocName] = useState("");

  useEffect(() => {
    if (open) {
      const latest = isCreateMode
        ? makeDefaultWeekly()
        : ((pid && lid) ? (schedules.get(`${pid}-${lid}`) || makeDefaultWeekly()) : makeDefaultWeekly());
      setDraft(normalizeDraft(latest));
      // fetch display names
      EmployeeService.getPractitioners().then(res => {
        if (res?.code === 0 && Array.isArray(res.data)) {
          const p = res.data.find(x => x.id === pid);
          setPrName(p?.fullName || `#${pid || ''}`);
        }
      }).catch(() => setPrName(pid ? `#${pid}` : ""));
      LocationService.getLocations().then(res => {
        if (res?.code === 0 && Array.isArray(res.data)) {
          const l = res.data.find(x => x.id === lid);
          setLocName(l?.name || `#${lid || ''}`);
        }
      }).catch(() => setLocName(lid ? `#${lid}` : ""));
    }
  }, [open, pid, lid, schedules, makeDefaultWeekly, normalizeDraft, isCreateMode]);

  const isNoRepeat = (draft?._meta?.frequency || "weekly") === "norepeat";
  const endMeta = draft?._meta?.end || { type: "on" };
  const effectiveEndDate = endMeta.date || toDateString(addDays(parseLocalDate(draft?._meta?.start || todayStr), 7));

  const toMinutes = (hhmm) => {
    if (!hhmm || typeof hhmm !== "string") return null;
    const [h, m] = hhmm.split(":").map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  };

  const recurringEnabledDays = useMemo(() => {
    const rows = [];
    for (let i = 0; i <= 6; i++) {
      if (draft?.[i]?.enabled) rows.push({ dow: i, start: draft?.[i]?.start, end: draft?.[i]?.end });
    }
    return rows;
  }, [draft]);

  const recurringDaysError = !isNoRepeat && recurringEnabledDays.length === 0 ? "Select at least one day" : "";
  const recurringTimeError = !isNoRepeat
    ? recurringEnabledDays.find((d) => {
        const startMin = toMinutes(d.start);
        const endMin = toMinutes(d.end);
        return startMin === null || endMin === null || endMin <= startMin;
      })
      ? "Each enabled day must have an end time after start time"
      : ""
    : "";

  const nonRepeatEntries = useMemo(
    () => (Array.isArray(draft?._meta?.nonRepeatEntries) ? draft._meta.nonRepeatEntries : []),
    [draft]
  );
  const nonRepeatRowErrors = useMemo(() => {
    if (!isNoRepeat) return [];
    return nonRepeatEntries.map((entry) => {
      if (!entry?.date) return "Date is required";
      if (!entry?.start || !entry?.end) return "Start and end time are required";
      const startMin = toMinutes(entry.start);
      const endMin = toMinutes(entry.end);
      if (startMin === null || endMin === null || endMin <= startMin) return "End time must be after start time";
      return "";
    });
  }, [isNoRepeat, nonRepeatEntries]);
  const nonRepeatError = isNoRepeat && nonRepeatEntries.length === 0 ? "Add at least one date" : "";
  const nonRepeatHasInvalidRow = nonRepeatRowErrors.some(Boolean);

  const endDateError = !isNoRepeat && endMeta.type === "on"
    ? (!effectiveEndDate ? "End date is required" : (effectiveEndDate < todayStr ? "End date cannot be in the past" : ""))
    : "";
  const occurrencesError = !isNoRepeat && endMeta.type === "after"
    ? (!Number.isFinite(Number(endMeta.count)) || Number(endMeta.count) < 1 ? "Occurrences must be at least 1" : "")
    : "";

  const formValid = !endDateError
    && !occurrencesError
    && !recurringDaysError
    && !recurringTimeError
    && !nonRepeatError
    && !nonRepeatHasInvalidRow;

  const handleSave = () => {
    const payloadDraft = {
      ...draft,
      _meta: {
        ...(draft?._meta || {}),
        end: !isNoRepeat && endMeta.type === "on"
          ? { ...endMeta, date: effectiveEndDate }
          : endMeta,
        nonRepeatEntries: isNoRepeat
          ? nonRepeatEntries.filter((entry) => entry?.date && entry?.start && entry?.end)
          : [],
      },
    };
    onSaveDraft({ pid, lid, draft: payloadDraft });
  };
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const startOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0=Sun
    const diff = d.getDate() - day;
    return new Date(d.getFullYear(), d.getMonth(), diff);
  };
  const formatHuman = (dateObj) => new Date(dateObj).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', weekday: 'short' });

  const seriesEndPreview = useMemo(() => {
    if (isNoRepeat) return "";
    try {
      const meta = draft?._meta || {};
      const freq = meta.frequency || 'weekly';
      const end = meta.end || { type: 'on', date: effectiveEndDate };
      const metaStartStr = meta.start || toLocalDateInputValue(new Date(), tenantTimezone);
      let seriesStart = parseLocalDate(metaStartStr);

      // Enabled weekdays
      const enabledDows = [];
      for (let i = 0; i <= 6; i++) {
        if (draft?.[i]?.enabled) enabledDows.push(i);
      }

      // Map frequency to repeatCount
      const repeatCount = (freq === 'biweekly') ? 2 : (freq === 'weekly' ? 1 : 0);

      if (end.type === 'on' && end.date) {
        return `Series end: ${formatHuman(parseLocalDate(end.date))}`;
      }
      if (end.type === 'never') {
        return `Series end: none`;
      }
      if (end.type === 'after') {
        const count = Number(end.count);
        if (!Number.isFinite(count) || count < 1) return '';

        const occWord = count === 1 ? 'occurrence' : 'occurrences';

        if (freq === 'daily') {
          const last = addDays(seriesStart, count - 1);
          return `Series end: ${formatHuman(last)} (after ${count} ${occWord})`;
        }

        if (enabledDows.length === 0) {
          return `Select at least one weekday to compute end date`;
        }

        // Align start to next enabled weekday (inclusive)
        const baseDow = seriesStart.getDay();
        let delta = 0;
        if (!enabledDows.includes(baseDow)) {
          delta = Math.min(...enabledDows.map(dow => (dow - baseDow + 7) % 7));
        }
        seriesStart = addDays(seriesStart, delta);

        const periodWeeks = repeatCount || 1; // 1 for weekly, 2 for biweekly
        const periodDays = periodWeeks * 7;
        const baseWeekStart = startOfWeek(seriesStart);
        let occurrencesFound = 0;
        let period = 0;
        let lastOccurrence = null;
        const sortedDows = [...enabledDows].sort((a,b)=>a-b);
        while (occurrencesFound < count && period < 520) { // safety
          for (const dow of sortedDows) {
            const occDate = addDays(addDays(baseWeekStart, period * periodDays), dow);
            if (occDate < seriesStart) continue;
            occurrencesFound++;
            lastOccurrence = occDate;
            if (occurrencesFound >= count) break;
          }
          period++;
        }
        if (!lastOccurrence) return '';
        return `Series end: ${formatHuman(lastOccurrence)} (after ${count} ${occWord})`;
      }
      return '';
    } catch {
      return '';
    }
  }, [draft, effectiveEndDate, isNoRepeat, tenantTimezone]);

  const addNonRepeatEntry = () => {
    setDraft((prev) => ({
      ...prev,
      _meta: {
        ...(prev?._meta || {}),
        nonRepeatEntries: [...(Array.isArray(prev?._meta?.nonRepeatEntries) ? prev._meta.nonRepeatEntries : []), defaultNonRepeatEntry()],
      },
    }));
  };

  const updateNonRepeatEntry = (idx, patch) => {
    setDraft((prev) => {
      const rows = Array.isArray(prev?._meta?.nonRepeatEntries) ? [...prev._meta.nonRepeatEntries] : [];
      rows[idx] = { ...(rows[idx] || {}), ...patch };
      return {
        ...prev,
        _meta: { ...(prev?._meta || {}), nonRepeatEntries: rows },
      };
    });
  };

  const removeNonRepeatEntry = (idx) => {
    setDraft((prev) => {
      const rows = Array.isArray(prev?._meta?.nonRepeatEntries) ? [...prev._meta.nonRepeatEntries] : [];
      rows.splice(idx, 1);
      return {
        ...prev,
        _meta: { ...(prev?._meta || {}), nonRepeatEntries: rows },
      };
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isCreateMode ? "Add Schedule" : "Edit Schedule"}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1 }}>Practitioner: {prName} — Location: {locName}</Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mr: 1 }}>Frequency:</Typography>
          <ToggleButtonGroup
            size="small"
            color="primary"
            exclusive
            value={draft?._meta?.frequency || 'weekly'}
            onChange={(_, val) => {
              if (!val) return;
              setDraft(prev => ({
                ...prev,
                _meta: {
                  ...(prev._meta || {}),
                  frequency: val,
                  nonRepeatEntries: val === "norepeat"
                    ? (Array.isArray(prev?._meta?.nonRepeatEntries) ? prev._meta.nonRepeatEntries : [])
                    : (prev?._meta?.nonRepeatEntries || []),
                }
              }));
            }}
          >
            <ToggleButton value="daily">Daily</ToggleButton>
            <ToggleButton value="weekly">Weekly</ToggleButton>
            <ToggleButton value="biweekly">Bi-weekly</ToggleButton>
            <ToggleButton value="norepeat">Do not repeat</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {isNoRepeat
            ? "Add one or more specific dates and times for non-recurring availability."
            : "Frequency and repeat options control recurring availability generation."}
        </Typography>
        {!isNoRepeat && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <SimpleDatePicker
            label="Start date"
            value={draft?._meta?.start || toLocalDateInputValue(new Date(), tenantTimezone)}
            onChange={(val) => setDraft(prev => ({
              ...prev,
              _meta: { ...(prev._meta || {}), start: val || '' }
            }))}
            helperText="Series will begin on or after this date"
          />
          </Stack>
        )}
        {!isNoRepeat && (
          <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Repeat ends</FormLabel>
          <RadioGroup
            row
            value={draft._meta?.end?.type || 'on'}
            onChange={(e) => {
              const type = e.target.value;
              setDraft(prev => ({
                ...prev,
                _meta: {
                  ...(prev._meta || {}),
                  end: type === 'never'
                    ? { type }
                    : (type === 'on'
                      ? { type, date: effectiveEndDate }
                      : { type, count: Math.max(1, Number(prev?._meta?.end?.count) || 10) }),
                }
              }));
            }}
          >
            <FormControlLabel value="on" control={<Radio size="small" />} label="On date" />
            <FormControlLabel value="after" control={<Radio size="small" />} label="After" />
          </RadioGroup>
          {draft._meta?.end?.type === 'on' && (
            <Stack direction="row" spacing={1} sx={{ mt: 1, maxWidth: 280 }}>
              <SimpleDatePicker
                label="End date"
                value={effectiveEndDate}
                minDate={todayStr}
                onChange={(val) => setDraft(prev => ({
                  ...prev,
                  _meta: {
                    ...(prev._meta || {}),
                    end: { ...(prev._meta?.end || {}), type: 'on', date: val || '' },
                  },
                }))}
                helperText={endDateError || " "}
              />
            </Stack>
          )}
          {draft._meta?.end?.type === 'after' && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <TextField
                size="small"
                type="number"
                label="Occurrences"
                inputProps={{ min: 1 }}
                value={draft._meta?.end?.count || 10}
                onChange={(e) => setDraft(prev => ({
                  ...prev,
                  _meta: { ...(prev._meta || {}), end: { ...(prev._meta?.end || {}), type: 'after', count: Math.max(1, Number(e.target.value) || 1) } }
                }))}
                error={Boolean(occurrencesError)}
                helperText={occurrencesError || ' '}
              />
              <Typography variant="caption" color="text.secondary">times</Typography>
            </Stack>
          )}
          {!!seriesEndPreview && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {seriesEndPreview}
            </Typography>
          )}
          </FormControl>
        )}

        {isNoRepeat && (
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              onClick={addNonRepeatEntry}
              startIcon={<Add />}
              sx={{ width: "fit-content" }}
            >
              Add a Date
            </Button>
            {!!nonRepeatError && (
              <Typography variant="caption" color="error">
                {nonRepeatError}
              </Typography>
            )}
            {nonRepeatEntries.map((entry, idx) => (
              <Stack key={`${entry.date || "date"}-${idx}`} spacing={0.75}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SimpleDatePicker
                    label="Date"
                    value={entry?.date || ""}
                    minDate={todayStr}
                    onChange={(val) => updateNonRepeatEntry(idx, { date: val || "" })}
                    helperTextMode="never"
                  />
                  <SimpleTimePicker
                    label="Start"
                    value={entry?.start || ""}
                    onChange={(val) => updateNonRepeatEntry(idx, { start: val || "" })}
                    step={15}
                    helperText=" "
                  />
                  <SimpleTimePicker
                    label="End"
                    value={entry?.end || ""}
                    onChange={(val) => updateNonRepeatEntry(idx, { end: val || "" })}
                    step={15}
                    minTime={entry?.start || null}
                    helperText=" "
                  />
                  <IconButton
                    aria-label="Remove date"
                    color="error"
                    onClick={() => removeNonRepeatEntry(idx)}
                    sx={{ mt: -2 }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </Stack>
                {!!nonRepeatRowErrors[idx] && (
                  <Typography variant="caption" color="error">
                    {nonRepeatRowErrors[idx]}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        )}

        <Divider sx={{ mb: 2 }} />
        {!isNoRepeat && (
          <Stack spacing={1}>
            {days.map((d, idx) => (
              <Stack key={d} direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={d} sx={{ width: 48 }} />
                <Checkbox
                  checked={!!draft[idx]?.enabled}
                  onChange={(e) => setDraft(prev => ({
                    ...prev,
                    [idx]: {
                      ...(prev[idx] || {}),
                      enabled: e.target.checked,
                      ...(e.target.checked ? {
                        start: (prev[idx]?.start || "09:00"),
                        end: (prev[idx]?.end || "17:00"),
                      } : {}),
                    },
                  }))}
                />
                <SimpleTimePicker
                  label="Start"
                  value={draft[idx]?.start || "09:00"}
                  onChange={(val) => setDraft({ ...draft, [idx]: { ...(draft[idx] || {}), start: val || "" } })}
                  step={15}
                  disabled={!draft[idx]?.enabled}
                  helperText=" "
                />
                <SimpleTimePicker
                  label="End"
                  value={draft[idx]?.end || "17:00"}
                  onChange={(val) => setDraft({ ...draft, [idx]: { ...(draft[idx] || {}), end: val || "" } })}
                  step={15}
                  minTime={draft[idx]?.start || null}
                  disabled={!draft[idx]?.enabled}
                  helperText=" "
                />
              </Stack>
            ))}
            {!!recurringDaysError && (
              <Typography variant="caption" color="error">
                {recurringDaysError}
              </Typography>
            )}
            {!!recurringTimeError && (
              <Typography variant="caption" color="error">
                {recurringTimeError}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleSave} disabled={!formValid}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
